import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Textarea from "../../components/common/Textarea";
import {
  useCashRefundPosOrderMutation,
  useGetFulfillmentOrderQuery,
  useLazyGetPosOrderPaymentsQuery,
  useRefundPosOrderMutation,
  useReturnVoidPosOrderMutation,
  useVoidPosOrderMutation,
} from "../../store/api";
import {
  createCashRefundIdempotencyKey,
  formatCashRefundMoney,
  getCashRefundError,
  getRecordedRefundReason,
  getRefundPaymentState,
} from "../../utils/cashRefund";
import { formatApiDateTime } from "../../utils/dateTime";

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;
const PAYMENT_METHOD_OPTIONS = [
  {
    value: "CASH",
    label: "Cash",
    helper: "Pay with cash",
    tone: "emerald",
    icon: (
      <svg
        className="h-5 w-5 text-emerald-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    value: "CARD",
    label: "Card",
    helper: "Credit or debit card",
    tone: "indigo",
    icon: (
      <svg
        className="h-5 w-5 text-indigo-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    ),
  },
];

const FulfillmentOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPendingPaymentView =
    searchParams.get("source") === "pending-payments";
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [activeAction, setActiveAction] = useState("VOID");
  const [actionReason, setActionReason] = useState(
    "Customer requested cancellation",
  );
  const [isCashRefundModalOpen, setIsCashRefundModalOpen] = useState(false);
  const [cashRefundPayments, setCashRefundPayments] = useState([]);
  const [cashRefundReasonCode, setCashRefundReasonCode] = useState("");
  const [cashRefundReasonError, setCashRefundReasonError] = useState("");
  const [cashRefundError, setCashRefundError] = useState("");
  const [cashRefundIdempotencyKey, setCashRefundIdempotencyKey] = useState("");
  const [refundPaymentMethod, setRefundPaymentMethod] = useState("CASH");
  const [refundResultModal, setRefundResultModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    tone: "success",
  });
  const [voidPosOrder, { isLoading: isVoiding }] = useVoidPosOrderMutation();
  const [returnVoidPosOrder, { isLoading: isReturnVoiding }] =
    useReturnVoidPosOrderMutation();
  const [loadCashRefundPayments, { isFetching: isLoadingCashPayments }] =
    useLazyGetPosOrderPaymentsQuery();
  const [cashRefundPosOrder, { isLoading: isCashRefunding }] =
    useCashRefundPosOrderMutation();
  const [refundPosOrder, { isLoading: isCardRefunding }] =
    useRefundPosOrderMutation();
  const isRefunding = isCashRefunding || isCardRefunding;

  const {
    data: order,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetFulfillmentOrderQuery(orderId, {
    skip: !orderId,
  });

  const lines = Array.isArray(order?.lines) ? order.lines : [];

  // Financial totals
  const subtotal = Number(order?.subtotal || 0);
  const taxAmount = Number(order?.taxAmount || 0);
  const discountAmount = Number(order?.discountAmount || 0);
  const grandTotal = Number(order?.grandTotal || 0);
  const orderNo = String(order?.orderNumber || order?.orderNo || "").trim();
  const cashRefundPaymentState = useMemo(
    () =>
      getRefundPaymentState(
        cashRefundPayments,
        grandTotal,
        refundPaymentMethod,
      ),
    [cashRefundPayments, grandTotal, refundPaymentMethod],
  );
  const cashRefundReason = cashRefundReasonCode.trim();
  const selectedRefundPaymentOption =
    PAYMENT_METHOD_OPTIONS.find(
      (option) => option.value === refundPaymentMethod,
    ) || PAYMENT_METHOD_OPTIONS[0];
  const isCashRefundFormDisabled =
    isLoadingCashPayments ||
    !cashRefundPaymentState.isEligible ||
    cashRefundPaymentState.hasRefund ||
    isRefunding;
  const normalizedStatus = String(order?.status || "").toUpperCase();
  const isProcessingAction = isVoiding || isReturnVoiding || isRefunding;
  const paymentStatusLabel = order?.paymentStatus || "-";
  const orderChannel = order?.channel || "-";
  const orderType = order?.orderType || "-";
  const masterTokenNo = String(
    order?.masterTokenNo || order?.masterToken || "",
  ).trim();
  const customerName = order?.customerName || "-";
  const customerContact = [order?.customerPhone, order?.customerEmail]
    .filter(Boolean)
    .join(" | ");
  const canVoid = Boolean(
    orderId &&
      !isLoading &&
      !isError &&
      grandTotal > 0 &&
      normalizedStatus !== "VOIDED",
  );
  const canReturnVoid = Boolean(
    orderId &&
      !isLoading &&
      !isError &&
      grandTotal > 0 &&
      normalizedStatus !== "RETURN_VOIDED",
  );

  const handleOpenActionModal = (actionType) => {
    const canOpen =
      (actionType === "VOID" && canVoid) ||
      (actionType === "RETURN_VOID" && canReturnVoid);
    if (!canOpen || isProcessingAction) return;
    setActionMessage("");
    setActionError("");
    setActiveAction(actionType);
    setIsActionModalOpen(true);
  };

  const handleOpenCashRefund = async () => {
    if (!orderNo || !orderId || isProcessingAction) return;
    setActionMessage("");
    setActionError("");
    setCashRefundError("");
    setCashRefundPayments([]);
    setCashRefundReasonCode("");
    setCashRefundReasonError("");
    setRefundPaymentMethod("CASH");
    setRefundResultModal({
      isOpen: false,
      title: "",
      message: "",
      tone: "success",
    });
    setCashRefundIdempotencyKey(createCashRefundIdempotencyKey());
    setIsCashRefundModalOpen(true);

    try {
      const paymentRows = await loadCashRefundPayments(orderId).unwrap();
      const normalizedPayments = Array.isArray(paymentRows) ? paymentRows : [];
      setCashRefundPayments(normalizedPayments);
      setCashRefundReasonCode(getRecordedRefundReason(normalizedPayments));
    } catch (loadError) {
      setCashRefundError(
        Number(loadError?.status) >= 500
          ? "Unable to load payment history. The refund cannot be verified until the payment service is available."
          : getCashRefundError(
              loadError,
              "Unable to load payment history for this order.",
            ),
      );
    }
  };

  const handleCloseCashRefund = () => {
    if (isRefunding) return;
    setIsCashRefundModalOpen(false);
    setCashRefundReasonError("");
    setCashRefundError("");
    setCashRefundIdempotencyKey("");
  };

  const handleConfirmCashRefund = async () => {
    if (!cashRefundReason) {
      setCashRefundReasonError("Refund reason is required.");
      return;
    }

    if (
      !orderId ||
      !orderNo ||
      !cashRefundPaymentState.isEligible ||
      isRefunding
    ) {
      return;
    }

    setCashRefundError("");
    try {
      const payload = {
        refundAmount: cashRefundPaymentState.refundableAmount,
        reason: cashRefundReason,
        idempotencyKey:
          cashRefundIdempotencyKey || createCashRefundIdempotencyKey(),
      };
      const refundRequest =
        refundPaymentMethod === "CARD"
          ? refundPosOrder({ orderId, payload })
          : cashRefundPosOrder({ orderId, orderNo, payload });
      await refundRequest.unwrap();
      setIsCashRefundModalOpen(false);
      setCashRefundIdempotencyKey("");
      setRefundResultModal({
        isOpen: true,
        title: "Refund Processed",
        message: "Refund processed successfully.",
        tone: "success",
      });
      await refetch();
    } catch (refundError) {
      const message = getCashRefundError(
        refundError,
        `Unable to process the ${refundPaymentMethod.toLowerCase()} refund.`,
      );
      setCashRefundError(message);
      setIsCashRefundModalOpen(false);
      setRefundResultModal({
        isOpen: true,
        title: "Refund Failed",
        message,
        tone: "error",
      });
    }
  };

  const handleCloseActionModal = () => {
    if (isProcessingAction) return;
    setIsActionModalOpen(false);
  };

  const getActionMeta = () => {
    if (activeAction === "RETURN_VOID") {
      return {
        title: "Return Void",
        confirmText: "Confirm Return Void",
        successMessage: "Return void processed successfully.",
        fallbackError: "Failed to process return void.",
      };
    }

    return {
      title: "Void Sale",
      confirmText: "Confirm Void",
      successMessage: "Void processed successfully.",
      fallbackError: "Failed to process void.",
    };
  };

  const handleConfirmAction = async () => {
    if (!orderId || isProcessingAction) return;
    setActionMessage("");
    setActionError("");

    try {
      const payload = {
        reason: actionReason.trim() || null,
        idempotencyKey:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      };

      if (activeAction === "RETURN_VOID") {
        await returnVoidPosOrder({ orderId, payload }).unwrap();
      } else {
        await voidPosOrder({ orderId, payload }).unwrap();
      }

      setIsActionModalOpen(false);
      setActionMessage(getActionMeta().successMessage);
      await refetch();
    } catch (err) {
      setActionError(
        err?.data?.detail || err?.data?.message || getActionMeta().fallbackError,
      );
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Order Detail"
        subtitle="Station tokens and line-level station mapping."
        actions={
          <div className="flex items-center gap-2">
            {!isPendingPaymentView && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  className="hidden"
                  onClick={() => handleOpenActionModal("VOID")}
                  disabled={!canVoid || isProcessingAction}
                  title={
                    canVoid
                      ? "Void same-day captured sale"
                      : "Void unavailable for this order"
                  }
                >
                  {isVoiding ? "Voiding..." : "Void Sale"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleOpenCashRefund}
                  disabled={!orderNo || isProcessingAction}
                  title="Review and confirm a refund"
                >
                  Refund
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="hidden"
                  onClick={() => handleOpenActionModal("RETURN_VOID")}
                  disabled={!canReturnVoid || isProcessingAction}
                  title={
                    canReturnVoid
                      ? "Void previously approved refund"
                      : "Return void unavailable for this order"
                  }
                >
                  {isReturnVoiding ? "Processing..." : "Return Void"}
                </Button>
              </>
            )}
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        }
      />

      <Modal
        isOpen={isActionModalOpen}
        onClose={handleCloseActionModal}
        title={getActionMeta().title}
        onConfirm={handleConfirmAction}
        confirmText={getActionMeta().confirmText}
        cancelText="Cancel"
        isLoading={isProcessingAction}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {activeAction === "VOID"
              ? "This will void the original captured sale for this order."
              : activeAction === "RETURN_VOID"
                ? "This will void the previously approved refund for this order."
                : "This will process a full refund for this order."}
          </p>
          <div className="space-y-2">
            <label
              htmlFor="action-reason"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              Reason (optional)
            </label>
            <textarea
              id="action-reason"
              rows={3}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder="Enter reason"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              disabled={isProcessingAction}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isCashRefundModalOpen}
        onClose={handleCloseCashRefund}
        title={`Refund${orderNo ? ` - Order ${orderNo}` : ""}`}
        onConfirm={handleConfirmCashRefund}
        confirmText="Confirm Refund"
        confirmVariant="danger"
        confirmDisabled={
          Boolean(cashRefundError) || isCashRefundFormDisabled
        }
        isLoading={isRefunding}
        size="md"
        align="center"
        footerStart={
          cashRefundPaymentState.refundableAmount > 0 ? (
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {formatCashRefundMoney(
                cashRefundPaymentState.refundableAmount,
                cashRefundPaymentState.currencyCode,
              )}
            </span>
          ) : null
        }
      >
        <div className="space-y-3">
          {cashRefundError ? (
            <Alert tone="error">{cashRefundError}</Alert>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                  Order Number
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {orderNo || "-"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                  Payment Method
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {selectedRefundPaymentOption.label}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                  Refund Amount
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {formatCashRefundMoney(
                    cashRefundPaymentState.refundableAmount,
                    cashRefundPaymentState.currencyCode,
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLoadingCashPayments ? (
            <Alert tone="info">Loading and verifying payment history...</Alert>
          ) : null}

          {!isLoadingCashPayments &&
          !cashRefundError &&
          !cashRefundPaymentState.isEligible ? (
            <Alert tone="warning">
              {cashRefundPaymentState.ineligibleReason}
            </Alert>
          ) : null}

          <Textarea
            label="Refund Reason"
            value={cashRefundReasonCode}
            onChange={(event) => {
              setCashRefundReasonCode(event.target.value);
              setCashRefundReasonError("");
              setCashRefundError("");
            }}
            error={cashRefundReasonError}
            placeholder="Enter the refund reason"
            autoComplete="off"
            maxLength={500}
            rows={4}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            required
            disabled={isCashRefundFormDisabled}
          />
        </div>
      </Modal>

      <Modal
        isOpen={refundResultModal.isOpen}
        onClose={() =>
          setRefundResultModal((current) => ({ ...current, isOpen: false }))
        }
        onConfirm={() =>
          setRefundResultModal((current) => ({ ...current, isOpen: false }))
        }
        title={refundResultModal.title}
        confirmText={refundResultModal.tone === "error" ? "Close" : "Done"}
        showCancel={false}
        confirmVariant={
          refundResultModal.tone === "error" ? "danger" : "primary"
        }
        size="md"
        align="center"
      >
        <div className="space-y-4">
          <div
            className={
              refundResultModal.tone === "error"
                ? "rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-800 dark:bg-rose-900/20"
                : "rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20"
            }
          >
            <p
              className={
                refundResultModal.tone === "error"
                  ? "text-sm font-semibold text-rose-800 dark:text-rose-200"
                  : "text-sm font-semibold text-emerald-800 dark:text-emerald-200"
              }
            >
              {refundResultModal.message}
            </p>
          </div>
          <dl className="divide-y divide-slate-200 rounded-xl border border-slate-200 px-4 dark:divide-slate-700 dark:border-slate-700">
            {[
              ["Order number", orderNo || "-"],
              ["Payment method", selectedRefundPaymentOption.label],
              [
                "Refund amount",
                formatCashRefundMoney(
                  cashRefundPaymentState.refundableAmount,
                  cashRefundPaymentState.currencyCode,
                ),
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-start justify-between gap-4 py-3 text-sm"
              >
                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                <dd className="text-right font-semibold text-slate-900 dark:text-slate-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Modal>

      {actionMessage ? <Alert tone="success">{actionMessage}</Alert> : null}
      {actionError ? <Alert tone="error">{actionError}</Alert> : null}

      {isLoading && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading order detail...
          </p>
        </Card>
      )}

      {isError && (
        <Alert tone="error">
          <div className="flex items-center justify-between gap-3">
            <span>
              {error?.data?.detail ||
                error?.data?.message ||
                "Failed to load order detail."}
            </span>
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {!isLoading && !isError && order && (
        <Card className="overflow-hidden p-0">
          {/* Order Header Info */}
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Order No
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {masterTokenNo || "-"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Channel
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {orderChannel}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Payment Status
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {paymentStatusLabel}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Order Type
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {orderType}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Customer
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {customerName}
                </div>
                {customerContact && (
                  <div className="mt-1 break-words text-xs text-slate-500 dark:text-slate-400">
                    {customerContact}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Placed At
                </div>
                <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {formatApiDateTime(order.placedAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Order Items and Summary - Two Column Layout */}
          <div className="p-4">
            <div className="mb-3">
              <h3 className="text-base font-medium text-slate-900 dark:text-slate-100">
                Order Items
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {lines.length} item{lines.length === 1 ? "" : "s"} in this order
              </p>
            </div>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
              {/* Left Column - Order Items */}
              <div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div className="hidden grid-cols-[minmax(0,1fr)_60px_88px_96px] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400 md:grid">
                    <span>Item</span>
                    <span className="text-right">Qty</span>
                    <span className="text-right">Unit Price</span>
                    <span className="text-right">Line Total</span>
                  </div>

                  <div className="max-h-[420px] overflow-y-auto">
                  {lines.length > 0 ? (
                    lines.map((line) => {
                      const modifiers = Array.isArray(line.modifiers)
                        ? line.modifiers
                        : Array.isArray(line.addOns)
                          ? line.addOns
                          : Array.isArray(line.addons)
                            ? line.addons
                            : [];
                      const modifiersText =
                        modifiers.length > 0
                          ? modifiers
                              .map((mod) => {
                                const modName =
                                  mod.name || mod.itemName || mod.addOnName || "Add-on";
                                const modPrice =
                                  Number(mod.priceDelta ?? mod.price ?? 0) > 0
                                    ? ` +$${Number(mod.priceDelta ?? mod.price ?? 0).toFixed(2)}`
                                    : "";
                                const modQty =
                                  Number(mod.qty ?? mod.quantity ?? 1) > 1
                                    ? ` x${mod.qty ?? mod.quantity}`
                                    : "";
                                return `${modName}${modPrice}${modQty}`;
                              })
                              .join(", ")
                          : "";
                      const quantity = Number(line.qty || 0);
                      const unitPrice = Number(line.unitPrice || 0);
                      const lineTotal = Number(line.lineTotal || 0);

                      return (
                        <div
                          key={
                            line.orderLineId ||
                            `${line.itemName}-${line.stationCode}`
                          }
                          className="border-b border-slate-100 px-3 py-1.5 last:border-b-0 dark:border-slate-800"
                        >
                          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_60px_88px_96px] md:items-start">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs text-slate-900 dark:text-slate-100">
                                  {line.itemName || "Item"}
                                </span>
                              </div>
                              {modifiersText && (
                                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 md:pr-4">
                                  <span className="font-medium">Add-ons:</span>{" "}
                                  {modifiersText}
                                </p>
                              )}
                              {line.notes && (
                                <p className="mt-1 text-[11px] italic text-amber-700 dark:text-amber-300">
                                  Note: {line.notes}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 md:block md:rounded-none md:bg-transparent md:px-0 md:py-0">
                              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 md:hidden">
                                Qty
                              </span>
                              <span className="text-xs text-slate-700 dark:text-slate-300 md:block md:text-right">
                                {quantity}
                              </span>
                            </div>

                            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 md:block md:rounded-none md:bg-transparent md:px-0 md:py-0">
                              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 md:hidden">
                                Unit Price
                              </span>
                              <span className="text-xs text-slate-700 dark:text-slate-300 md:block md:text-right">
                                {formatCurrency(unitPrice)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between rounded-lg bg-slate-900 px-3 py-1.5 text-white md:block md:rounded-none md:bg-transparent md:px-0 md:py-0 md:text-right">
                              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-300 md:hidden">
                                Line Total
                              </span>
                              <span className="text-xs font-normal md:text-slate-900 md:dark:text-slate-100">
                                {formatCurrency(lineTotal)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-10 text-center">
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No items available
                      </p>
                    </div>
                  )}
                  </div>
                </div>
              </div>

            {/* Right Column - Order Summary */}
            <div>
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
                <div className="border-b border-slate-200 pb-3 dark:border-slate-700">
                  <h3 className="text-base font-medium text-slate-900 dark:text-slate-100">
                    Order Summary
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Final breakdown for this ticket.
                  </p>
                </div>

                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                      <span>Discount</span>
                      <span>
                        -{formatCurrency(discountAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                    <span>Tax</span>
                    <span>{formatCurrency(taxAmount)}</span>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-slate-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-600 dark:text-slate-300">
                        Grand Total
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Includes taxes and applied adjustments
                      </div>
                    </div>
                    <div className="text-xl text-slate-900 dark:text-slate-100">
                      {formatCurrency(grandTotal)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FulfillmentOrderDetail;
